import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Archive, CalendarPlus, Copy, GearSix, HardDrives, MapPin } from "@phosphor-icons/react";
import { aender, lies, schick } from "../../api/client";
import type { Frist, Projekt, Uebersicht } from "../../api/types";
import { Auswahl } from "../../components/Auswahl";
import { Dialog, Feld, Meldung } from "../../components/Bausteine";
import { Ortssuche } from "../../components/Ortssuche";
import { datum, euro } from "../../lib/format";
import { Standort } from "./TagebuchReiter";

const ZUSTAND: Record<Frist["zustand"], [string, string]> = {
  ueberfaellig: ["überfällig", "fehler"], heute: ["heute", "warn"], bald: ["bald", "akzent"], spaeter: ["", ""],
};
const ART: Record<string, string> = {
  punkt: "Protokollpunkt", mangel: "Mangel", fiktive_abnahme: "Fiktive Abnahme", gewaehrleistung: "Gewährleistungsbegehung",
  gewaehrleistung_ende: "Ende Mängelansprüche", besprechung: "Besprechung", anzeige: "Anzeige",
};
export const BUNDESLAND: Record<string, string> = {
  BW: "Baden-Württemberg", BY: "Bayern", BE: "Berlin", BB: "Brandenburg", HB: "Bremen", HH: "Hamburg",
  HE: "Hessen", MV: "Mecklenburg-Vorpommern", NI: "Niedersachsen", NW: "Nordrhein-Westfalen",
  RP: "Rheinland-Pfalz", SL: "Saarland", SN: "Sachsen", ST: "Sachsen-Anhalt", SH: "Schleswig-Holstein", TH: "Thüringen",
};
const STATUS: Record<Projekt["status"], string> = {
  vorbereitung: "Vorbereitung", ausfuehrung: "Ausführung", maengelhaftung: "Mängelhaftung", abgeschlossen: "abgeschlossen",
};
type Fortfuehren = NonNullable<Projekt["erledigte_fortfuehren"]>;
/** Wanderung 023: ob ein erledigter Punkt im nächsten Protokoll noch steht. */
const FORTFUEHREN: Record<Fortfuehren, string> = {
  nein: "nicht weiterführen – nur im Protokoll, das sie erledigt",
  einmal: "einmal weiterführen – noch im nächsten Protokoll",
  immer: "immer weiterführen – in allen folgenden Protokollen",
};

/** Projekt-Startseite (SPEC §15): was ist offen, was ist fällig. */
export function UebersichtReiter({ projekt }: { projekt: Projekt }) {
  const u = useQuery({ queryKey: ["uebersicht", projekt.id], queryFn: () => lies<Uebersicht>(`/projekte/${projekt.id}/uebersicht`) });
  const f = useQuery({ queryKey: ["fristen", projekt.id], queryFn: () => lies<Frist[]>(`/projekte/${projekt.id}/fristen`) });
  const [einstellungen, setEinstellungen] = useState(false);
  const leitung = projekt.meine_rolle === "leitung";
  const d = u.data;
  const fristen = (f.data ?? []).filter((x) => x.zustand !== "spaeter");
  const spaeter = (f.data ?? []).filter((x) => x.zustand === "spaeter");

  return (
    <>
      {d && (
        <div className="kennzahlen">
          <Kachel zahl={d.punkte.offen} titel="offene Protokollpunkte" warn={d.punkte.ueberfaellig}
                  nach={`/p/${projekt.kurz}/besprechungen`} />
          <Kachel zahl={d.maengel.reduce((s, m) => s + m.offen, 0)} titel="offene Mängel"
                  warn={d.maengel.reduce((s, m) => s + m.ueberfaellig, 0)} nach={`/p/${projekt.kurz}/maengel`} />
          <Kachel zahl={d.anzeigen.offen} titel="offene Anzeigen" warn={d.anzeigen.ueberfaellig} nach={`/p/${projekt.kurz}/anzeigen`} />
          <Link className="kennzahl" to={`/p/${projekt.kurz}/anzeigen`}>
            <div className="kennzahl-zahl klein-zahl">{euro(d.nachtraege.beauftragt) || "0,00 €"}</div>
            <div className="mute">Nachträge beauftragt{d.nachtraege.anzahl_offen > 0 && `, ${d.nachtraege.anzahl_offen} offen (${euro(d.nachtraege.offen)})`}</div>
          </Link>
        </div>
      )}
      <Meldung fehler={u.error || f.error} />

      <section className="karte">
        <div className="karte-kopf">
          <h2>Fristen</h2>
          <span className="mute">überfällig und in den nächsten Tagen · Bundesland {BUNDESLAND[projekt.bundesland ?? "HH"]}</span>
          <div className="luft" />
          <KalenderAbo />
        </div>
        <div className="liste">
          {fristen.map((x) => <FristZeile key={`${x.art}-${x.bezug_id}`} f={x} />)}
          {f.isSuccess && fristen.length === 0 && <div className="leer">Nichts überfällig, nichts in den nächsten Tagen fällig.</div>}
        </div>
        {spaeter.length > 0 && (
          <details className="spaeter">
            <summary className="mute">{spaeter.length} weitere Fristen später</summary>
            <div className="liste">{spaeter.map((x) => <FristZeile key={`${x.art}-${x.bezug_id}`} f={x} />)}</div>
          </details>
        )}
      </section>

      {d && d.maengel.length > 0 && (
        <section className="karte">
          <div className="karte-kopf"><h2>Offene Mängel je Auftragnehmer</h2></div>
          <div className="liste">
            {d.maengel.map((m) => (
              <div key={m.wer} className="zeile" style={{ minHeight: 44 }}>
                <span className="nr ziffern">{m.offen}</span>
                <div className="haupt"><div>{m.wer}</div></div>
                <span style={{ display: "flex", gap: 6 }}>
                  {m.wesentlich > 0 && <span className="chip fehler">{m.wesentlich} wesentlich</span>}
                  {m.ueberfaellig > 0 && <span className="chip warn">{m.ueberfaellig} über Frist</span>}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="karte">
        <div className="karte-kopf">
          <h2>Projekt</h2>
          <span className="mute">{STATUS[projekt.status]}{projekt.lat != null && " · Standort hinterlegt"}</span>
          <div className="luft" />
          {leitung && <a className="knopf" href={`/api/projekte/${projekt.id}/archiv.zip`}
                         title="Alle Unterlagen als ZIP: Ausgang, Protokolle, Begehungen, Bautagebuch, Listen, Fotos, Pläne">
            <Archive size={16} /> Archiv (ZIP)</a>}
          {leitung && <button className="knopf" onClick={() => setEinstellungen(true)}><GearSix size={16} /> Einstellungen</button>}
        </div>
        <div className="mute">
          {d?.letzte_besprechung && <>Letzte Besprechung Nr. {d.letzte_besprechung.nummer} am {datum(d.letzte_besprechung.datum)}
            {d.letzte_besprechung.naechste_am && `, nächste am ${datum(d.letzte_besprechung.naechste_am)}`}. </>}
          {d?.letzter_tag && <>Bautagebuch zuletzt {datum(d.letzter_tag)}.</>}
        </div>
      </section>
      {einstellungen && <Einstellungen projekt={projekt} zu={() => setEinstellungen(false)} />}
    </>
  );
}

function Kachel({ zahl, titel, warn, nach }: { zahl: number; titel: string; warn: number; nach: string }) {
  return (
    <Link className={`kennzahl${warn ? " kennzahl-warn" : ""}`} to={nach}>
      <div className="kennzahl-zahl">{zahl}</div>
      <div className="mute">{titel}{warn > 0 && <b>, {warn} überfällig</b>}</div>
    </Link>
  );
}

function FristZeile({ f }: { f: Frist }) {
  const [text, farbe] = ZUSTAND[f.zustand];
  return (
    <Link to={f.link} className="zeile">
      <span className="nr ziffern">{datum(f.datum)}</span>
      <div className="haupt">
        <div>{f.titel}</div>
        <div className="mute">{[ART[f.art] ?? f.art, f.wer].filter(Boolean).join(" · ")}</div>
      </div>
      {text && <span className={`chip ${farbe}`}>{text}</span>}
    </Link>
  );
}

/** Fristen als Kalender-Abo (ICS, §12). Die Adresse ist geheim und wird nur einmal gezeigt. */
function KalenderAbo() {
  const [url, setUrl] = useState("");
  const [kopiert, setKopiert] = useState(false);
  const stand = useQuery({ queryKey: ["kalender"], queryFn: () => lies<{ eingerichtet: boolean }>("/me/kalender") });
  const anlegen = useMutation({ mutationFn: () => schick<{ url: string }>("/me/kalender"), onSuccess: (r) => setUrl(r.url) });
  return (
    <>
      <button className="knopf leise" title="Fristen aller Projekte in Outlook oder auf dem Telefon"
              onClick={() => (!stand.data?.eingerichtet || confirm("Neue Kalenderadresse erzeugen? Die bisherige gilt danach nicht mehr.")) && anlegen.mutate()}>
        <CalendarPlus size={16} /> Kalender abonnieren</button>
      {url && (
        <Dialog offen zu={() => setUrl("")} titel="Kalender abonnieren" fuss={<button className="knopf" onClick={() => setUrl("")}>Fertig</button>}>
          <p style={{ marginTop: 0 }}>Diese Adresse in Outlook unter <b>Kalender hinzufügen → Aus dem Internet</b> eintragen
            (am iPhone: Einstellungen → Kalender → Accounts → Kalenderabo). Sie enthält die Fristen aller deiner Projekte
            und wird nur jetzt angezeigt – wie ein Passwort behandeln.</p>
          <div className="punkt-leiste">
            <input className="eingabe" readOnly value={url} onFocus={(e) => e.target.select()} />
            <button className="knopf" onClick={async () => { await navigator.clipboard.writeText(url); setKopiert(true); }}>
              <Copy size={16} /> {kopiert ? "kopiert" : "Kopieren"}</button>
          </div>
        </Dialog>
      )}
      <Meldung fehler={anlegen.error} />
    </>
  );
}

function Einstellungen({ projekt, zu }: { projekt: Projekt; zu: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: projekt.name, nummer: projekt.nummer, ort: projekt.ort, bauherr: projekt.bauherr, status: projekt.status,
    einwand_tage: String(projekt.einwand_tage), bundesland: projekt.bundesland ?? "HH",
    lat: projekt.lat != null ? String(projekt.lat) : "", lon: projekt.lon != null ? String(projekt.lon) : "",
    ablage_protokolle: projekt.ablage_protokolle ?? "", ablage_schreiben: projekt.ablage_schreiben ?? "",
    erledigte_fortfuehren: projekt.erledigte_fortfuehren ?? "nein",
  });
  const zahl = (s: string) => (s.trim() ? Number(s.replace(",", ".")) : undefined);
  const speichern = useMutation({
    mutationFn: () => aender<Projekt>(`/projekte/${projekt.id}`, {
      ...f, einwand_tage: Number(f.einwand_tage), lat: zahl(f.lat), lon: zahl(f.lon),
    }),
    onSuccess: () => { for (const k of [["projekt"], ["projekte"], ["fristen"], ["uebersicht"]]) qc.invalidateQueries({ queryKey: k }); zu(); },
  });
  return (
    <Dialog offen zu={zu} titel="Projekt-Einstellungen" fuss={<>
      <button className="knopf leise" onClick={zu}>Abbrechen</button>
      <button className="knopf haupt" disabled={!f.name.trim() || speichern.isPending} onClick={() => speichern.mutate()}>Speichern</button>
    </>}>
      <div className="raster">
        <Feld titel="Projektname" breit><input className="eingabe" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Feld>
        <Feld titel="Projektnummer"><input className="eingabe" value={f.nummer} onChange={(e) => setF({ ...f, nummer: e.target.value })} /></Feld>
        <Feld titel="Ort"><input className="eingabe" value={f.ort} onChange={(e) => setF({ ...f, ort: e.target.value })} /></Feld>
        <Feld titel="Bauherr"><input className="eingabe" value={f.bauherr} onChange={(e) => setF({ ...f, bauherr: e.target.value })} /></Feld>
        <Feld titel="Phase">
          <Auswahl value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as Projekt["status"] })}>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        <Feld titel="Bundesland (Feiertage für Werktagsfristen)">
          <Auswahl value={f.bundesland} onChange={(e) => setF({ ...f, bundesland: e.target.value })}>
            {Object.entries(BUNDESLAND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        <Feld titel="Einwandfrist (Kalendertage)">
          <input className="eingabe" type="number" min={0} max={60} value={f.einwand_tage} onChange={(e) => setF({ ...f, einwand_tage: e.target.value })} />
        </Feld>
        <Feld titel="Erledigte Punkte in Folgeprotokollen" breit>
          <Auswahl value={f.erledigte_fortfuehren}
                   onChange={(e) => setF({ ...f, erledigte_fortfuehren: e.target.value as Fortfuehren })}>
            {Object.entries(FORTFUEHREN).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Auswahl>
        </Feld>
        <div className="feld breit"><span>Ort der Baustelle (für Wetter und Karte)</span>
          <Ortssuche gewaehlt={(o) => setF((x) => ({ ...x, lat: String(o.lat), lon: String(o.lon), ort: x.ort || o.ort }))} />
        </div>
        <Feld titel="Breitengrad"><input className="eingabe" inputMode="decimal" placeholder="z. B. 54,90"
                                                          value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} /></Feld>
        <Feld titel="Längengrad"><input className="eingabe" inputMode="decimal" placeholder="z. B. 8,31"
                                        value={f.lon} onChange={(e) => setF({ ...f, lon: e.target.value })} /></Feld>
      </div>
      {projekt.lat == null && <div style={{ marginTop: 12 }}><Standort projekt={projekt} /></div>}
      <p className="mute"><MapPin size={13} /> Ohne Treffer: in Google Maps Rechtsklick auf die Baustelle – die erste Zahl ist der Breiten-, die zweite der Längengrad.</p>
      <h3 className="dialog-abschnitt"><HardDrives size={16} /> Ablage auf dem Büro-Server</h3>
      <p className="mute" style={{ marginTop: 0 }}>Beim Versand legt die Anwendung eine PDF-Kopie in diesen Ordner. Den Pfad so
        eintragen, wie er im Explorer steht (Adressleiste kopieren).</p>
      <div className="raster">
        <OrdnerFeld projektId={projekt.id} titel="Ordner für Protokolle" wert={f.ablage_protokolle}
                    setze={(v) => setF((x) => ({ ...x, ablage_protokolle: v }))} />
        <OrdnerFeld projektId={projekt.id} titel="Ordner für Niederschriften und Mängelanzeigen (leer = wie Protokolle)"
                    wert={f.ablage_schreiben} setze={(v) => setF((x) => ({ ...x, ablage_schreiben: v }))} />
      </div>
      <Meldung fehler={speichern.error} />
    </Dialog>
  );
}

/** Ein Ordner auf dem Büro-Server (SPEC §11.4) mit „Prüfen": gibt es ihn, darf der Server
 *  hineinschreiben? Ist die Ablage auf diesem Server noch nicht eingerichtet, steht es dabei. */
function OrdnerFeld({ projektId, titel, wert, setze }: { projektId: string; titel: string; wert: string; setze: (v: string) => void }) {
  const status = useQuery({ queryKey: ["serverablage"], queryFn: () => lies<{ aktiv: boolean; laufwerk: string }>("/serverablage/status"),
                            staleTime: Infinity });
  const pruefen = useMutation({
    mutationFn: () => schick<{ ok: boolean; dateien: number }>(`/projekte/${projektId}/serverablage/pruefen`, { pfad: wert }),
  });
  const laufwerk = status.data?.laufwerk ?? "Y:\\";
  return (
    <div className="feld breit ablage-feld">
      <span>{titel}</span>
      <div className="punkt-leiste" style={{ flexWrap: "nowrap" }}>
        <input className="eingabe" value={wert} placeholder={`${laufwerk}Projekte\\2111_Projekt\\05_Bauleitung\\Protokolle`}
               onChange={(e) => { setze(e.target.value); pruefen.reset(); }} />
        <button type="button" className="knopf" disabled={!wert.trim() || !status.data?.aktiv || pruefen.isPending}
                onClick={() => pruefen.mutate()}>Prüfen</button>
      </div>
      {status.data && !status.data.aktiv && wert.trim() &&
        <span className="mute">Die Ablage auf dem Büro-Server ist auf dem Server noch nicht eingerichtet – der Pfad wird gespeichert und gilt, sobald sie es ist.</span>}
      {pruefen.data && <span className="gespeichert">Ordner gefunden, Schreiben möglich ({pruefen.data.dateien} PDFs darin).</span>}
      <Meldung fehler={pruefen.error} />
    </div>
  );
}
